import React, { useEffect, useMemo, useState } from 'react';
import {
  FALLBACK_CONTACT_SETTINGS,
  getPublicContactSettings,
} from '../features/contactSettings';
import { withTimeout } from '../shared/lib/withTimeout';

type DonorsInfoButtonVariant = 'floating' | 'inline';

export interface DonorsInfoButtonProps {
  variant?: DonorsInfoButtonVariant;
}

const CONTACT_SETTINGS_TIMEOUT_MS = 2500;
const INLINE_DARK_GREEN = '#064e3b';
const INLINE_DARK_GREEN_HOVER = '#065f46';

const DonorsInfoButton: React.FC<DonorsInfoButtonProps> = ({ variant = 'floating' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);
  const [donorTitle, setDonorTitle] = useState(FALLBACK_CONTACT_SETTINGS.donorTitle);
  const [donorDescriptionHtml, setDonorDescriptionHtml] = useState(
    FALLBACK_CONTACT_SETTINGS.donorDescriptionHtml
  );

  useEffect(() => {
    withTimeout(getPublicContactSettings(), CONTACT_SETTINGS_TIMEOUT_MS, 'Contact settings timeout')
      .then((settings) => {
        setDonorTitle(settings.donorTitle);
        setDonorDescriptionHtml(settings.donorDescriptionHtml);
      })
      .catch((err) => {
        console.warn('Failed to load public contact settings for donors panel', err);
        setDonorTitle(FALLBACK_CONTACT_SETTINGS.donorTitle);
        setDonorDescriptionHtml(FALLBACK_CONTACT_SETTINGS.donorDescriptionHtml);
      });
  }, []);

  useEffect(() => {
    if (isOpen) setIsPulsing(false);
  }, [isOpen]);

  const buttonPositionStyle = useMemo(() => {
    if (variant === 'floating') {
      return {
        bottom: '20px',
        right: '20px',
        zIndex: 1000,
      } as const;
    }
    return undefined;
  }, [variant]);

  const buttonClassName = useMemo(() => {
    const base = `btn btn-donors-info d-flex align-items-center justify-content-center gap-2 ${
      isPulsing ? 'pulse-animation' : ''
    } ${isOpen ? 'active' : ''}`;
    if (variant === 'floating') return `${base} position-fixed`;
    return `${base} btn-lg rounded-pill`;
  }, [isOpen, isPulsing, variant]);

  const buttonTheme = useMemo(() => {
    if (variant === 'inline') {
      return {
        background: INLINE_DARK_GREEN,
        boxShadow: '0 4px 15px rgba(6, 78, 59, 0.35)',
      } as const;
    }
    return {
      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
      boxShadow: '0 4px 15px rgba(231, 76, 60, 0.4)',
    } as const;
  }, [variant]);

  const controlsId = variant === 'inline' ? 'donors-info-modal' : 'donors-info-panel';

  return (
    <>
      {/* Trigger Button */}
      <button
        className={buttonClassName}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        style={{
          ...(buttonPositionStyle ?? {}),
          borderRadius: '50px',
          padding: variant === 'floating' ? '16px 24px' : '14px 22px',
          background: buttonTheme.background,
          border: 'none',
          boxShadow: buttonTheme.boxShadow,
          color: 'white',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          transform: isOpen ? 'scale(0.96)' : 'scale(1)',
        }}
        aria-expanded={isOpen}
        aria-controls={controlsId}
      >
        <span className="floating-icon">
          <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-heart-fill'}`} />
        </span>
        <span className={variant === 'floating' ? 'd-none d-sm-inline' : ''}>
          {isOpen ? 'Close' : 'About Donors'}
        </span>

        {!isOpen && (
          <>
            <span className="sparkle sparkle-1" />
            <span className="sparkle sparkle-2" />
            <span className="sparkle sparkle-3" />
          </>
        )}
      </button>

      {variant === 'inline' ? (
        <div
          className={`modal ${isOpen ? 'show' : ''}`}
          style={{
            display: isOpen ? 'block' : 'none',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1060,
          }}
          role="dialog"
          aria-modal="true"
          aria-hidden={!isOpen}
          aria-label={donorTitle || 'Donors information'}
          onClick={() => setIsOpen(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-3d" id={controlsId}>
              <div
                className="modal-header text-white"
                style={{
                  background: `linear-gradient(135deg, ${INLINE_DARK_GREEN} 0%, #0f1f12 100%)`,
                }}
              >
                <h5 className="modal-title d-flex align-items-center gap-2 mb-0" style={{ minWidth: 0 }}>
                  <i className="bi bi-heart-fill" />
                  <span style={{ wordBreak: 'break-word' }}>{donorTitle}</span>
                </h5>
                <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => setIsOpen(false)} />
              </div>
              <div className="modal-body donors-panel-content">
                <div
                  className="rich-text-content text-muted"
                  dangerouslySetInnerHTML={{ __html: donorDescriptionHtml }}
                  style={{
                    lineHeight: 1.8,
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%',
                  }}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setIsOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Donors Panel Overlay */}
          <div
            className={`donors-panel-overlay position-fixed ${isOpen ? 'show' : ''}`}
            onClick={() => setIsOpen(false)}
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 999,
              opacity: isOpen ? 1 : 0,
              visibility: isOpen ? 'visible' : 'hidden',
              transition: 'all 0.3s ease',
            }}
            aria-hidden={!isOpen}
          />

          {/* Donors Panel */}
          <div
            id={controlsId}
            className={`donors-panel position-fixed bg-white shadow-3d ${isOpen ? 'show' : ''}`}
            style={{
              bottom: '100px',
              right: '20px',
              width: 'clamp(300px, 90vw, 400px)',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: '70vh',
              borderRadius: '20px',
              zIndex: 1001,
              transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
              opacity: isOpen ? 1 : 0,
              visibility: isOpen ? 'visible' : 'hidden',
              transition: 'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
            role="dialog"
            aria-modal="true"
            aria-label={donorTitle || 'Donors information'}
          >
            <div
              className="position-relative p-4 text-white"
              style={{
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                boxSizing: 'border-box',
              }}
            >
              <div className="donors-pattern" />

              <div className="position-relative" style={{ zIndex: 2 }}>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <div
                    className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 50, height: 50, flexShrink: 0 }}
                  >
                    <i className="bi bi-heart-fill fs-4" />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 className="h5 mb-0 fw-bold" style={{ wordBreak: 'break-word' }}>
                      {donorTitle}
                    </h3>
                    <span className="small opacity-75">Our valued supporters</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="p-4 donors-panel-content"
              style={{
                maxHeight: 'calc(70vh - 120px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                boxSizing: 'border-box',
                width: '100%',
              }}
            >
              <div
                className="rich-text-content text-muted"
                dangerouslySetInnerHTML={{ __html: donorDescriptionHtml }}
                style={{
                  lineHeight: 1.8,
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '100%',
                }}
              />
            </div>

            <div
              className="position-absolute donors-panel-arrow"
              style={{
                bottom: '-10px',
                right: '40px',
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: '10px solid white',
              }}
            />
          </div>
        </>
      )}

      <style>{`
        .btn-donors-info:hover {
          transform: scale(1.03) !important;
          box-shadow: 0 6px 20px rgba(231, 76, 60, 0.5) !important;
        }

        .btn-donors-info.active {
          background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%) !important;
          box-shadow: 0 4px 15px rgba(127, 140, 141, 0.4) !important;
        }

        .btn-donors-info:not(.position-fixed):hover {
          background: ${INLINE_DARK_GREEN_HOVER} !important;
          box-shadow: 0 6px 20px rgba(6, 78, 59, 0.45) !important;
        }

        .pulse-animation {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 4px 15px rgba(231, 76, 60, 0.4);
          }
          50% {
            box-shadow: 0 4px 25px rgba(231, 76, 60, 0.7), 0 0 30px rgba(231, 76, 60, 0.3);
          }
        }

        .floating-icon {
          animation: heartbeat 1.5s ease-in-out infinite;
          display: inline-block;
        }

        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          14% { transform: scale(1.1); }
          28% { transform: scale(1); }
          42% { transform: scale(1.1); }
          70% { transform: scale(1); }
        }

        .btn-donors-info.active .floating-icon {
          animation: none;
        }

        .sparkle {
          position: absolute;
          width: 10px;
          height: 10px;
          background: white;
          border-radius: 50%;
          opacity: 0;
        }

        .sparkle-1 {
          top: -5px;
          left: -5px;
          animation: sparkle 2s ease-in-out infinite;
        }

        .sparkle-2 {
          top: -5px;
          right: -5px;
          animation: sparkle 2s ease-in-out infinite 0.5s;
        }

        .sparkle-3 {
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          animation: sparkle 2s ease-in-out infinite 1s;
        }

        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1) rotate(180deg); }
        }

        .shadow-3d {
          box-shadow:
            0 10px 40px rgba(0, 0, 0, 0.2),
            0 2px 8px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .donors-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.1;
          background-image:
            radial-gradient(circle at 25% 25%, white 2%, transparent 2%),
            radial-gradient(circle at 75% 75%, white 2%, transparent 2%);
          background-size: 30px 30px;
          z-index: 1;
        }

        @media (max-width: 576px) {
          .donors-panel {
            right: 10px !important;
            left: 10px !important;
            width: auto !important;
            max-width: calc(100vw - 20px) !important;
          }
          .donors-panel-content { max-width: 100% !important; }
          .donors-panel-arrow { display: none; }
        }
      `}</style>
    </>
  );
};

export default DonorsInfoButton;
